<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const store = useStore();

const allFields = computed(() =>
  [...(store.getters['ticketFieldSettings/getTicketFieldSettings'] || [])]
    .sort((a, b) => a.position - b.position)
);

// Main list: hide conditional fields — they are managed inside the parent field's Conditional Sections panel
const fields = computed(() => allFields.value.filter(f => !f.conditions?.show_when));
const uiFlags = computed(() => store.getters['ticketFieldSettings/getTicketFieldUIFlags']);

// ── Sections: which custom fields are conditioned on each system field option ──
const sectionsByFieldAndValue = computed(() => {
  // { [parent_field_key]: { [value]: [ field, ... ] } }
  const result = {};
  allFields.value.forEach(f => {
    if (f.is_system) return;
    const sw = f.conditions?.show_when;
    if (!sw) return;
    if (!result[sw.field_key]) result[sw.field_key] = {};
    if (!result[sw.field_key][sw.value]) result[sw.field_key][sw.value] = [];
    result[sw.field_key][sw.value].push(f);
  });
  return result;
});

// Conditional fields whose show_when.value doesn't match any current option on the parent field
// (e.g. if a Type option was renamed/deleted after the conditional field was created)
const orphanedSectionsFor = field => {
  const knownValues = new Set((field.options || []).map(o => o.value));
  return allFields.value.filter(
    f => !f.is_system &&
      f.conditions?.show_when?.field_key === field.field_key &&
      !knownValues.has(f.conditions.show_when.value)
  );
};

// ── New field form ──────────────────────────────────────────────
const showAddField = ref(false);
const addFieldDialogRef = ref(null);
const editFieldDialogRef = ref(null);
const addFieldPrefillCondition = ref(null); // { field_key, value } — pre-sets condition when adding from sections
const newField = ref({ label: '', field_type: 'dropdown', options: [], conditions: {} });

const ticketTypeOptions = computed(() => fields.value.find(f => f.field_key === 'ticket_type')?.options || []);

const openAddFieldWithCondition = (fieldKey, value) => {
  addFieldPrefillCondition.value = { field_key: fieldKey, value };
  newField.value = {
    label: '',
    field_type: 'dropdown',
    options: [],
    conditions: { show_when: { field_key: fieldKey, value } },
  };
  newSelectedL1Idx.value = null;
  newSelectedL2Idx.value = null;
  newSelectedL3Idx.value = null;
  newL2Inputs.value = {};
  newL3Inputs.value = {};
  newL4Inputs.value = {};
  showAddField.value = true;
  addFieldDialogRef.value?.open();
};
const newOptionLabel = ref('');

// 4-level column state for new form
const newSelectedL1Idx = ref(null);
const newSelectedL2Idx = ref(null);
const newSelectedL3Idx = ref(null);
const newL2Inputs = ref({});
const newL3Inputs = ref({});
const newL4Inputs = ref({});

const addNewOption = () => {
  const label = newOptionLabel.value.trim();
  if (!label) return;
  newField.value.options = [...newField.value.options, {
    value: label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, ''),
    label,
    level2_label: '',
    children: [],
  }];
  newOptionLabel.value = '';
};

const removeNewOption = idx => {
  newField.value.options = newField.value.options.filter((_, i) => i !== idx);
  if (newSelectedL1Idx.value === idx) { newSelectedL1Idx.value = null; newSelectedL2Idx.value = null; newSelectedL3Idx.value = null; }
  else if (newSelectedL1Idx.value !== null && newSelectedL1Idx.value > idx) newSelectedL1Idx.value--;
};

const updateNewLevel2Label = (l1Idx, val) => {
  const opts = [...newField.value.options];
  opts[l1Idx] = { ...opts[l1Idx], level2_label: val };
  newField.value.options = opts;
};

const addNewL2Option = l1Idx => {
  const label = (newL2Inputs.value[l1Idx] || '').trim();
  if (!label) return;
  const opts = [...newField.value.options];
  opts[l1Idx] = {
    ...opts[l1Idx],
    children: [...(opts[l1Idx].children || []), { value: label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, ''), label, level3_label: '', children: [] }],
  };
  newField.value.options = opts;
  newL2Inputs.value = { ...newL2Inputs.value, [l1Idx]: '' };
};

const removeNewL2Option = (l1Idx, l2Idx) => {
  const opts = [...newField.value.options];
  opts[l1Idx] = { ...opts[l1Idx], children: (opts[l1Idx].children || []).filter((_, i) => i !== l2Idx) };
  newField.value.options = opts;
  if (newSelectedL2Idx.value === l2Idx) { newSelectedL2Idx.value = null; newSelectedL3Idx.value = null; }
  else if (newSelectedL2Idx.value !== null && newSelectedL2Idx.value > l2Idx) newSelectedL2Idx.value--;
};

const updateNewLevel3Label = (l1Idx, l2Idx, val) => {
  const opts = [...newField.value.options];
  const children = [...(opts[l1Idx].children || [])];
  children[l2Idx] = { ...children[l2Idx], level3_label: val };
  opts[l1Idx] = { ...opts[l1Idx], children };
  newField.value.options = opts;
};

const addNewL3Option = (l1Idx, l2Idx) => {
  const key = `${l1Idx}_${l2Idx}`;
  const label = (newL3Inputs.value[key] || '').trim();
  if (!label) return;
  const opts = [...newField.value.options];
  const children = [...(opts[l1Idx].children || [])];
  children[l2Idx] = {
    ...children[l2Idx],
    children: [...(children[l2Idx].children || []), { value: label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, ''), label, level4_label: '', children: [] }],
  };
  opts[l1Idx] = { ...opts[l1Idx], children };
  newField.value.options = opts;
  newL3Inputs.value = { ...newL3Inputs.value, [key]: '' };
};

const removeNewL3Option = (l1Idx, l2Idx, l3Idx) => {
  const opts = [...newField.value.options];
  const children = [...(opts[l1Idx].children || [])];
  children[l2Idx] = { ...children[l2Idx], children: (children[l2Idx].children || []).filter((_, i) => i !== l3Idx) };
  opts[l1Idx] = { ...opts[l1Idx], children };
  newField.value.options = opts;
  if (newSelectedL3Idx.value === l3Idx) newSelectedL3Idx.value = null;
  else if (newSelectedL3Idx.value !== null && newSelectedL3Idx.value > l3Idx) newSelectedL3Idx.value--;
};

const updateNewLevel4Label = (l1Idx, l2Idx, l3Idx, val) => {
  const opts = [...newField.value.options];
  const l2ch = [...(opts[l1Idx].children || [])];
  const l3ch = [...(l2ch[l2Idx].children || [])];
  l3ch[l3Idx] = { ...l3ch[l3Idx], level4_label: val };
  l2ch[l2Idx] = { ...l2ch[l2Idx], children: l3ch };
  opts[l1Idx] = { ...opts[l1Idx], children: l2ch };
  newField.value.options = opts;
};

const addNewL4Option = (l1Idx, l2Idx, l3Idx) => {
  const key = `${l1Idx}_${l2Idx}_${l3Idx}`;
  const label = (newL4Inputs.value[key] || '').trim();
  if (!label) return;
  const opts = [...newField.value.options];
  const l2ch = [...(opts[l1Idx].children || [])];
  const l3ch = [...(l2ch[l2Idx].children || [])];
  l3ch[l3Idx] = { ...l3ch[l3Idx], children: [...(l3ch[l3Idx].children || []), { value: label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, ''), label }] };
  l2ch[l2Idx] = { ...l2ch[l2Idx], children: l3ch };
  opts[l1Idx] = { ...opts[l1Idx], children: l2ch };
  newField.value.options = opts;
  newL4Inputs.value = { ...newL4Inputs.value, [key]: '' };
};

const removeNewL4Option = (l1Idx, l2Idx, l3Idx, l4Idx) => {
  const opts = [...newField.value.options];
  const l2ch = [...(opts[l1Idx].children || [])];
  const l3ch = [...(l2ch[l2Idx].children || [])];
  l3ch[l3Idx] = { ...l3ch[l3Idx], children: (l3ch[l3Idx].children || []).filter((_, i) => i !== l4Idx) };
  l2ch[l2Idx] = { ...l2ch[l2Idx], children: l3ch };
  opts[l1Idx] = { ...opts[l1Idx], children: l2ch };
  newField.value.options = opts;
};

const saveNewField = async () => {
  if (!newField.value.label.trim()) return;
  const prefillCondition = addFieldPrefillCondition.value;
  try {
    await store.dispatch('ticketFieldSettings/create', {
      label: newField.value.label,
      field_type: newField.value.field_type,
      options: newField.value.options,
      conditions: newField.value.conditions,
      enabled: true,
      position: fields.value.length,
    });
    newField.value = { label: '', field_type: 'dropdown', options: [], conditions: {} };
    addFieldPrefillCondition.value = null;
    newSelectedL1Idx.value = null;
    newSelectedL2Idx.value = null;
    newSelectedL3Idx.value = null;
    newL2Inputs.value = {};
    newL3Inputs.value = {};
    newL4Inputs.value = {};
    showAddField.value = false;
    addFieldDialogRef.value?.close();
    useAlert('Field created successfully');
    // Re-open the parent field's edit form so the user can see the new conditional field
    if (prefillCondition) {
      const parentField = allFields.value.find(f => f.field_key === prefillCondition.field_key);
      if (parentField) startEdit(parentField);
    }
  } catch {
    useAlert('Failed to create field');
  }
};

// ── Edit field ──────────────────────────────────────────────────
const editingId = ref(null);
const editState = ref({});
const editOptionInput = ref('');

// 4-level column state for edit form
const editSelectedL1Idx = ref(null);
const editSelectedL2Idx = ref(null);
const editSelectedL3Idx = ref(null);
const editL2Inputs = ref({});
const editL3Inputs = ref({});
const editL4Inputs = ref({});

const startEdit = field => {
  editingId.value = field.id;
  editingConditionalField.value = field.conditions?.show_when ? field : null;
  if (editingConditionalField.value) editFieldDialogRef.value?.open();
  editState.value = {
    label: field.label,
    enabled: field.enabled,
    conditions: field.conditions ? { ...field.conditions } : {},
    options: field.options
      ? field.options.map(o => ({
          ...o,
          level2_label: o.level2_label || o.sub_label || '',
          children: (o.children || []).map(c => ({
            ...c,
            level3_label: c.level3_label || '',
            children: (c.children || []).map(g => ({
            ...g,
            level4_label: g.level4_label || '',
            children: (g.children || []).map(l4 => ({ ...l4 })),
          })),
          })),
        }))
      : [],
  };
  editOptionInput.value = '';
  editSelectedL1Idx.value = null;
  editSelectedL2Idx.value = null;
  editSelectedL3Idx.value = null;
  editL2Inputs.value = {};
  editL3Inputs.value = {};
  editL4Inputs.value = {};
};

// Holds the conditional field being edited (if any). Set directly in startEdit so
// the modal appears even though conditional fields are absent from the main v-for.
const editingConditionalField = ref(null);

const cancelEdit = () => {
  editingId.value = null;
  editingConditionalField.value = null;
  editFieldDialogRef.value?.close();
};

const addEditOption = () => {
  const label = editOptionInput.value.trim();
  if (!label) return;
  editState.value.options = [
    ...editState.value.options,
    { value: label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, ''), label, level2_label: '', children: [], custom: true },
  ];
  editOptionInput.value = '';
};

const removeEditOption = idx => {
  editState.value.options = editState.value.options.filter((_, i) => i !== idx);
  if (editSelectedL1Idx.value === idx) { editSelectedL1Idx.value = null; editSelectedL2Idx.value = null; editSelectedL3Idx.value = null; }
  else if (editSelectedL1Idx.value !== null && editSelectedL1Idx.value > idx) editSelectedL1Idx.value--;
};

const updateOptionLabel = (idx, val) => {
  const opts = [...editState.value.options];
  opts[idx] = { ...opts[idx], label: val };
  editState.value.options = opts;
};

const updateEditLevel2Label = (l1Idx, val) => {
  const opts = [...editState.value.options];
  opts[l1Idx] = { ...opts[l1Idx], level2_label: val };
  editState.value.options = opts;
};

const addEditL2Option = l1Idx => {
  const label = (editL2Inputs.value[l1Idx] || '').trim();
  if (!label) return;
  const opts = [...editState.value.options];
  opts[l1Idx] = {
    ...opts[l1Idx],
    children: [...(opts[l1Idx].children || []), { value: label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, ''), label, level3_label: '', children: [] }],
  };
  editState.value.options = opts;
  editL2Inputs.value = { ...editL2Inputs.value, [l1Idx]: '' };
};

const removeEditL2Option = (l1Idx, l2Idx) => {
  const opts = [...editState.value.options];
  opts[l1Idx] = { ...opts[l1Idx], children: (opts[l1Idx].children || []).filter((_, i) => i !== l2Idx) };
  editState.value.options = opts;
  if (editSelectedL2Idx.value === l2Idx) { editSelectedL2Idx.value = null; editSelectedL3Idx.value = null; }
  else if (editSelectedL2Idx.value !== null && editSelectedL2Idx.value > l2Idx) editSelectedL2Idx.value--;
};

const updateEditLevel3Label = (l1Idx, l2Idx, val) => {
  const opts = [...editState.value.options];
  const children = [...(opts[l1Idx].children || [])];
  children[l2Idx] = { ...children[l2Idx], level3_label: val };
  opts[l1Idx] = { ...opts[l1Idx], children };
  editState.value.options = opts;
};

const addEditL3Option = (l1Idx, l2Idx) => {
  const key = `${l1Idx}_${l2Idx}`;
  const label = (editL3Inputs.value[key] || '').trim();
  if (!label) return;
  const opts = [...editState.value.options];
  const children = [...(opts[l1Idx].children || [])];
  children[l2Idx] = {
    ...children[l2Idx],
    children: [...(children[l2Idx].children || []), { value: label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, ''), label, level4_label: '', children: [] }],
  };
  opts[l1Idx] = { ...opts[l1Idx], children };
  editState.value.options = opts;
  editL3Inputs.value = { ...editL3Inputs.value, [key]: '' };
};

const removeEditL3Option = (l1Idx, l2Idx, l3Idx) => {
  const opts = [...editState.value.options];
  const children = [...(opts[l1Idx].children || [])];
  children[l2Idx] = { ...children[l2Idx], children: (children[l2Idx].children || []).filter((_, i) => i !== l3Idx) };
  opts[l1Idx] = { ...opts[l1Idx], children };
  editState.value.options = opts;
  if (editSelectedL3Idx.value === l3Idx) editSelectedL3Idx.value = null;
  else if (editSelectedL3Idx.value !== null && editSelectedL3Idx.value > l3Idx) editSelectedL3Idx.value--;
};

const updateEditLevel4Label = (l1Idx, l2Idx, l3Idx, val) => {
  const opts = [...editState.value.options];
  const l2ch = [...(opts[l1Idx].children || [])];
  const l3ch = [...(l2ch[l2Idx].children || [])];
  l3ch[l3Idx] = { ...l3ch[l3Idx], level4_label: val };
  l2ch[l2Idx] = { ...l2ch[l2Idx], children: l3ch };
  opts[l1Idx] = { ...opts[l1Idx], children: l2ch };
  editState.value.options = opts;
};

const addEditL4Option = (l1Idx, l2Idx, l3Idx) => {
  const key = `${l1Idx}_${l2Idx}_${l3Idx}`;
  const label = (editL4Inputs.value[key] || '').trim();
  if (!label) return;
  const opts = [...editState.value.options];
  const l2ch = [...(opts[l1Idx].children || [])];
  const l3ch = [...(l2ch[l2Idx].children || [])];
  l3ch[l3Idx] = { ...l3ch[l3Idx], children: [...(l3ch[l3Idx].children || []), { value: label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, ''), label }] };
  l2ch[l2Idx] = { ...l2ch[l2Idx], children: l3ch };
  opts[l1Idx] = { ...opts[l1Idx], children: l2ch };
  editState.value.options = opts;
  editL4Inputs.value = { ...editL4Inputs.value, [key]: '' };
};

const removeEditL4Option = (l1Idx, l2Idx, l3Idx, l4Idx) => {
  const opts = [...editState.value.options];
  const l2ch = [...(opts[l1Idx].children || [])];
  const l3ch = [...(l2ch[l2Idx].children || [])];
  l3ch[l3Idx] = { ...l3ch[l3Idx], children: (l3ch[l3Idx].children || []).filter((_, i) => i !== l4Idx) };
  l2ch[l2Idx] = { ...l2ch[l2Idx], children: l3ch };
  opts[l1Idx] = { ...opts[l1Idx], children: l2ch };
  editState.value.options = opts;
};

const saveEdit = async id => {
  try {
    await store.dispatch('ticketFieldSettings/update', {
      id,
      label: editState.value.label,
      enabled: editState.value.enabled,
      conditions: editState.value.conditions,
      options: editState.value.options,
    });
    editingId.value = null;
    useAlert('Field updated');
  } catch {
    useAlert('Failed to update field');
  }
};

const toggleEnabled = async field => {
  try {
    await store.dispatch('ticketFieldSettings/update', {
      id: field.id,
      label: field.label,
      enabled: !field.enabled,
      options: field.options,
      conditions: field.conditions,
    });
  } catch {
    useAlert('Failed to update field');
  }
};

const deleteField = async field => {
  if (!confirm(`Delete field "${field.label}"? This cannot be undone.`)) return;
  try {
    await store.dispatch('ticketFieldSettings/delete', field.id);
    useAlert('Field deleted');
  } catch {
    useAlert('Failed to delete field');
  }
};

const fieldTypeLabel = type => ({ text: 'Text', dropdown: 'Dropdown', date: 'Date', number: 'Number' }[type] || type);

// ── Drag-and-drop reordering (main list) ────────────────────────
const dragSrcId = ref(null);
const dragOverId = ref(null);

// ── Drag-and-drop reordering (conditional sections) ─────────────
const condDragSrcId = ref(null);
const condDragOverId = ref(null);

const buildFullOrderedIds = newCondOrder => {
  const newOrderIds = new Set(newCondOrder.map(f => f.id));
  const allSorted = [...allFields.value].sort((a, b) => a.position - b.position);
  const result = [];
  let inserted = false;
  for (const f of allSorted) {
    if (newOrderIds.has(f.id)) {
      if (!inserted) { result.push(...newCondOrder); inserted = true; }
    } else {
      result.push(f);
    }
  }
  if (!inserted) result.push(...newCondOrder);
  return result.map(f => f.id);
};

const onCondDragStart = (e, sf) => {
  condDragSrcId.value = sf.id;
  e.dataTransfer.effectAllowed = 'move';
};
const onCondDragOver = (e, sf) => {
  e.preventDefault();
  e.dataTransfer.dropEffect = 'move';
  condDragOverId.value = sf.id;
};
const onCondDragLeave = () => { condDragOverId.value = null; };
const onCondDrop = async (e, targetSf, allSfsInSection) => {
  e.preventDefault();
  condDragOverId.value = null;
  if (!condDragSrcId.value || condDragSrcId.value === targetSf.id) return;
  const arr = [...allSfsInSection];
  const srcIdx = arr.findIndex(f => f.id === condDragSrcId.value);
  const tgtIdx = arr.findIndex(f => f.id === targetSf.id);
  if (srcIdx === -1 || tgtIdx === -1) return;
  const [moved] = arr.splice(srcIdx, 1);
  arr.splice(tgtIdx, 0, moved);
  try {
    await store.dispatch('ticketFieldSettings/reorder', buildFullOrderedIds(arr));
  } catch {
    useAlert('Failed to reorder fields');
  }
  condDragSrcId.value = null;
};
const onCondDragEnd = () => { condDragSrcId.value = null; condDragOverId.value = null; };

// ── Drag-and-drop reordering for dropdown choices ────────────────
const optDragSrc = ref({ level: null, idx: null });
const optDragOver = ref({ level: null, idx: null });

const onOptDragStart = (e, level, idx) => {
  optDragSrc.value = { level, idx };
  e.dataTransfer.effectAllowed = 'move';
};
const onOptDragOver = (e, level, idx) => {
  e.preventDefault();
  e.dataTransfer.dropEffect = 'move';
  optDragOver.value = { level, idx };
};
const onOptDragLeave = () => { optDragOver.value = { level: null, idx: null }; };
const onOptDragEnd = () => {
  optDragSrc.value = { level: null, idx: null };
  optDragOver.value = { level: null, idx: null };
};

const reorderArr = (arr, src, tgt) => {
  const a = [...arr];
  const [moved] = a.splice(src, 1);
  a.splice(tgt, 0, moved);
  return a;
};

const fixSelectedIdx = (selRef, src, tgt) => {
  if (selRef.value === src) { selRef.value = tgt; return; }
  if (selRef.value === null) return;
  if (src < selRef.value && tgt >= selRef.value) selRef.value--;
  else if (src > selRef.value && tgt <= selRef.value) selRef.value++;
};

const onEditOptDrop = (e, level, tgtIdx) => {
  e.preventDefault();
  optDragOver.value = { level: null, idx: null };
  const srcIdx = optDragSrc.value.idx;
  optDragSrc.value = { level: null, idx: null };
  if (srcIdx === null || srcIdx === tgtIdx) return;

  if (level === 1) {
    editState.value.options = reorderArr(editState.value.options, srcIdx, tgtIdx);
    fixSelectedIdx(editSelectedL1Idx, srcIdx, tgtIdx);
  } else if (level === 2) {
    const l1 = editSelectedL1Idx.value;
    const opts = [...editState.value.options];
    opts[l1] = { ...opts[l1], children: reorderArr(opts[l1]?.children || [], srcIdx, tgtIdx) };
    editState.value.options = opts;
    fixSelectedIdx(editSelectedL2Idx, srcIdx, tgtIdx);
  } else if (level === 3) {
    const l1 = editSelectedL1Idx.value; const l2 = editSelectedL2Idx.value;
    const opts = [...editState.value.options];
    const c1 = [...(opts[l1]?.children || [])];
    c1[l2] = { ...c1[l2], children: reorderArr(c1[l2]?.children || [], srcIdx, tgtIdx) };
    opts[l1] = { ...opts[l1], children: c1 };
    editState.value.options = opts;
    fixSelectedIdx(editSelectedL3Idx, srcIdx, tgtIdx);
  } else if (level === 4) {
    const l1 = editSelectedL1Idx.value; const l2 = editSelectedL2Idx.value; const l3 = editSelectedL3Idx.value;
    const opts = [...editState.value.options];
    const c1 = [...(opts[l1]?.children || [])];
    const c2 = [...(c1[l2]?.children || [])];
    c2[l3] = { ...c2[l3], children: reorderArr(c2[l3]?.children || [], srcIdx, tgtIdx) };
    c1[l2] = { ...c1[l2], children: c2 };
    opts[l1] = { ...opts[l1], children: c1 };
    editState.value.options = opts;
  }
};

const onNewOptDrop = (e, level, tgtIdx) => {
  e.preventDefault();
  optDragOver.value = { level: null, idx: null };
  const srcIdx = optDragSrc.value.idx;
  optDragSrc.value = { level: null, idx: null };
  if (srcIdx === null || srcIdx === tgtIdx) return;

  if (level === 1) {
    newField.value.options = reorderArr(newField.value.options, srcIdx, tgtIdx);
    fixSelectedIdx(newSelectedL1Idx, srcIdx, tgtIdx);
  } else if (level === 2) {
    const l1 = newSelectedL1Idx.value;
    const opts = [...newField.value.options];
    opts[l1] = { ...opts[l1], children: reorderArr(opts[l1]?.children || [], srcIdx, tgtIdx) };
    newField.value.options = opts;
    fixSelectedIdx(newSelectedL2Idx, srcIdx, tgtIdx);
  } else if (level === 3) {
    const l1 = newSelectedL1Idx.value; const l2 = newSelectedL2Idx.value;
    const opts = [...newField.value.options];
    const c1 = [...(opts[l1]?.children || [])];
    c1[l2] = { ...c1[l2], children: reorderArr(c1[l2]?.children || [], srcIdx, tgtIdx) };
    opts[l1] = { ...opts[l1], children: c1 };
    newField.value.options = opts;
    fixSelectedIdx(newSelectedL3Idx, srcIdx, tgtIdx);
  } else if (level === 4) {
    const l1 = newSelectedL1Idx.value; const l2 = newSelectedL2Idx.value; const l3 = newSelectedL3Idx.value;
    const opts = [...newField.value.options];
    const c1 = [...(opts[l1]?.children || [])];
    const c2 = [...(c1[l2]?.children || [])];
    c2[l3] = { ...c2[l3], children: reorderArr(c2[l3]?.children || [], srcIdx, tgtIdx) };
    c1[l2] = { ...c1[l2], children: c2 };
    opts[l1] = { ...opts[l1], children: c1 };
    newField.value.options = opts;
  }
};

const onDragStart = (e, field) => {
  dragSrcId.value = field.id;
  e.dataTransfer.effectAllowed = 'move';
};

const onDragOver = (e, field) => {
  e.preventDefault();
  e.dataTransfer.dropEffect = 'move';
  dragOverId.value = field.id;
};

const onDragLeave = () => {
  dragOverId.value = null;
};

const onDrop = async (e, targetField) => {
  e.preventDefault();
  dragOverId.value = null;
  if (!dragSrcId.value || dragSrcId.value === targetField.id) return;

  const sorted = [...fields.value];
  const srcIdx = sorted.findIndex(f => f.id === dragSrcId.value);
  const tgtIdx = sorted.findIndex(f => f.id === targetField.id);
  if (srcIdx === -1 || tgtIdx === -1) return;

  const [moved] = sorted.splice(srcIdx, 1);
  sorted.splice(tgtIdx, 0, moved);

  try {
    await store.dispatch('ticketFieldSettings/reorder', sorted.map(f => f.id));
  } catch {
    useAlert('Failed to reorder fields');
  }
  dragSrcId.value = null;
};

const onDragEnd = () => {
  dragSrcId.value = null;
  dragOverId.value = null;
};

onMounted(() => store.dispatch('ticketFieldSettings/fetchAll'));
</script>

<template>
  <div class="mx-auto max-w-5xl px-6 py-8">
    <!-- Header -->
    <div class="flex items-center justify-between mb-6">
      <div>
        <h1 class="text-xl font-semibold text-slate-900 dark:text-slate-100">Ticket Fields</h1>
        <p class="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
          Manage fields shown in the Ticket Properties panel. Drag to reorder, toggle visibility, edit labels, or delete fields.
        </p>
      </div>
      <button
        class="btn-add-blue mr-4"
        @click="showAddField = true; addFieldDialogRef?.open()"
      >+ Add Field</button>
    </div>

    <!-- Loading -->
    <div v-if="uiFlags.isFetching" class="text-center py-12 text-slate-400">Loading fields…</div>

    <!-- Field list -->
    <div v-else class="space-y-3">
      <div
        v-for="field in fields"
        :key="field.id"
        draggable="true"
        class="rounded-xl border bg-white dark:bg-slate-900 overflow-hidden transition-all"
        :class="[
          !field.enabled ? 'opacity-60' : '',
          dragOverId === field.id ? 'border-woot-400 ring-2 ring-woot-200 dark:ring-woot-800' : 'border-slate-200 dark:border-slate-700',
          dragSrcId === field.id ? 'opacity-40' : '',
        ]"
        @dragstart="onDragStart($event, field)"
        @dragover="onDragOver($event, field)"
        @dragleave="onDragLeave"
        @drop="onDrop($event, field)"
        @dragend="onDragEnd"
      >
        <!-- ── View row ── -->
        <div v-if="editingId !== field.id" class="flex items-center gap-3 px-4 py-3">
          <span class="i-lucide-grip-vertical size-4 text-slate-400 dark:text-slate-500 shrink-0 cursor-grab active:cursor-grabbing" />
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2">
              <span class="text-sm font-medium text-slate-800 dark:text-slate-200">{{ field.label }}</span>
              <span class="text-xs px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400">
                {{ fieldTypeLabel(field.field_type) }}
              </span>
              <span v-if="field.is_system" class="text-xs px-1.5 py-0.5 rounded bg-blue-50 dark:bg-blue-900/30 text-blue-500">
                System
              </span>
              <span v-if="field.conditions?.show_when" class="text-xs px-1.5 py-0.5 rounded bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400">
                When Type = {{ field.conditions.show_when.value }}
              </span>
            </div>
            <div v-if="field.field_type === 'dropdown' && field.options?.length" class="mt-1 flex flex-wrap gap-1">
              <span
                v-for="opt in field.options"
                :key="opt.value"
                class="text-xs px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400"
              >
                {{ opt.label }}
                <span v-if="opt.children?.length" class="text-slate-400 dark:text-slate-500 ml-0.5">▸{{ opt.children.length }}</span>
              </span>
            </div>
          </div>
          <div class="flex items-center gap-2 shrink-0">
            <button
              :title="field.enabled ? 'Hide field' : 'Show field'"
              class="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
              :class="field.enabled ? 'text-green-500' : 'text-slate-400'"
              @click="toggleEnabled(field)"
            >
              <span :class="field.enabled ? 'i-lucide-eye' : 'i-lucide-eye-off'" class="size-4" />
            </button>
            <button
              class="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 hover:text-woot-500"
              title="Edit field"
              @click="startEdit(field)"
            >
              <span class="i-lucide-pencil size-4" />
            </button>
            <button
              class="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-slate-400 hover:text-red-500"
              title="Delete field"
              @click="deleteField(field)"
            >
              <span class="i-lucide-trash-2 size-4" />
            </button>
          </div>
        </div>

        <!-- ── Edit row ── -->
        <div v-else class="p-4 space-y-4 bg-slate-50 dark:bg-slate-800/50">
          <!-- Label -->
          <div>
            <label class="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Field Label</label>
            <input
              v-model="editState.label"
              type="text"
              class="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-woot-500"
            />
          </div>

          <!-- Enabled toggle -->
          <div class="flex items-center gap-3">
            <label class="text-xs font-semibold text-slate-500 uppercase tracking-wide">Visible in Ticket Properties</label>
            <button
              class="relative inline-flex h-5 w-9 items-center rounded-full transition-colors"
              :class="editState.enabled ? 'bg-woot-500' : 'bg-slate-300 dark:bg-slate-600'"
              @click="editState.enabled = !editState.enabled"
            >
              <span
                class="inline-block size-4 rounded-full bg-white shadow transition-transform"
                :class="editState.enabled ? 'translate-x-4' : 'translate-x-0.5'"
              />
            </button>
          </div>

          <!-- Visibility Condition -->
          <div v-if="!field.is_system" class="rounded-lg border border-slate-200 dark:border-slate-600 p-3 space-y-2">
            <p class="text-xs font-semibold text-slate-500 uppercase tracking-wide">Visibility Condition</p>
            <div class="flex items-center gap-2">
              <button
                class="relative inline-flex h-5 w-9 items-center rounded-full transition-colors shrink-0"
                :class="editState.conditions?.show_when ? 'bg-woot-500' : 'bg-slate-300 dark:bg-slate-600'"
                @click="editState.conditions = editState.conditions?.show_when ? {} : { show_when: { field_key: 'ticket_type', value: ticketTypeOptions[0]?.value || '' } }"
              >
                <span class="inline-block size-4 rounded-full bg-white shadow transition-transform" :class="editState.conditions?.show_when ? 'translate-x-4' : 'translate-x-0.5'" />
              </button>
              <span class="text-xs text-slate-500">Only show when another field matches</span>
            </div>
            <div v-if="editState.conditions?.show_when" class="flex items-center gap-2 pl-1">
              <span class="text-xs text-slate-500">Show when Type is</span>
              <select
                :value="editState.conditions.show_when.value"
                class="px-2 py-1 text-xs rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-woot-500"
                @change="editState.conditions = { show_when: { field_key: 'ticket_type', value: $event.target.value } }"
              >
                <option v-for="opt in ticketTypeOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
              </select>
            </div>
          </div>

          <!-- Dropdown options — cascaded layout -->
          <div v-if="field.field_type === 'dropdown'">
            <label class="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Dropdown Options</label>
            <p class="text-xs text-slate-400 mb-3">
              The <strong class="text-slate-500">Field Label</strong> above is shown as a non-selectable header in Ticket Properties.
              Add choices here — when an agent selects a choice, the next level appears below it.
              Click any choice to configure its sub-choices and set each level's label.
            </p>

            <div class="flex gap-3 overflow-x-auto pb-1">

              <!-- Column 1: Level 1 choices (shown under the field label) -->
              <div class="flex-1 min-w-[170px] flex flex-col rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 overflow-hidden">
                <div class="px-3 py-2 bg-slate-50 dark:bg-slate-700 border-b border-slate-200 dark:border-slate-600">
                  <p class="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Choices under "{{ editState.label || 'Field Label' }}"</p>
                </div>
                <div class="flex-1 overflow-y-auto max-h-52 py-1">
                  <div
                    v-for="(opt, idx) in editState.options"
                    :key="idx"
                    draggable="true"
                    class="group flex items-center gap-1 px-2 py-1.5 cursor-pointer transition-colors"
                    :class="[
                      editSelectedL1Idx === idx ? 'bg-woot-50 dark:bg-woot-900/30' : 'hover:bg-slate-50 dark:hover:bg-slate-700/50',
                      optDragOver.level === 1 && optDragOver.idx === idx ? 'ring-1 ring-woot-400' : '',
                      optDragSrc.level === 1 && optDragSrc.idx === idx ? 'opacity-40' : '',
                    ]"
                    @click="editSelectedL1Idx = idx; editSelectedL2Idx = null; editSelectedL3Idx = null"
                    @dragstart="onOptDragStart($event, 1, idx)"
                    @dragover="onOptDragOver($event, 1, idx)"
                    @dragleave="onOptDragLeave"
                    @drop="onEditOptDrop($event, 1, idx)"
                    @dragend="onOptDragEnd"
                  >
                    <span class="i-lucide-grip-vertical size-3 text-slate-400 cursor-grab shrink-0" />
                    <span class="flex-1 min-w-0 text-sm text-slate-700 dark:text-slate-200 truncate">{{ opt.label }}</span>
                    <span v-if="opt.children?.length" class="text-xs text-woot-400 shrink-0">▸{{ opt.children.length }}</span>
                    <span v-if="editSelectedL1Idx === idx" class="text-xs text-woot-500 shrink-0 font-bold">›</span>
                    <button
                      class="p-0.5 text-slate-300 hover:text-red-500 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                      @click.stop="removeEditOption(idx)"
                    >
                      <span class="i-lucide-x size-3" />
                    </button>
                  </div>
                  <p v-if="!editState.options.length" class="text-xs text-slate-400 italic text-center py-4">No choices yet</p>
                </div>
                <!-- Add L1 -->
                <div class="border-t border-slate-200 dark:border-slate-600 px-2 py-2 flex gap-1">
                  <input
                    v-model="editOptionInput"
                    type="text"
                    placeholder="Add choice…"
                    class="flex-1 min-w-0 px-2 py-1 text-xs rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-woot-500"
                    @keydown.enter.prevent="addEditOption"
                  />
                  <button
                    class="px-2 py-1 text-xs font-medium text-woot-600 border border-woot-200 rounded hover:bg-woot-50 dark:hover:bg-woot-900/20 shrink-0"
                    @click="addEditOption"
                  >+</button>
                </div>
              </div>

              <!-- Column 2: Level 2 choices (shown when L1 is selected) -->
              <div v-if="editSelectedL1Idx !== null" class="flex-1 min-w-[185px] flex flex-col rounded-lg border border-woot-200 dark:border-woot-700 bg-white dark:bg-slate-800 overflow-hidden">
                <div class="px-3 py-2 bg-woot-50 dark:bg-woot-900/20 border-b border-woot-200 dark:border-woot-700 space-y-1.5">
                  <p class="text-xs font-semibold text-woot-600 dark:text-woot-400 uppercase tracking-wide">
                    Level 2 — under "{{ editState.options[editSelectedL1Idx]?.label }}"
                  </p>
                  <input
                    :value="editState.options[editSelectedL1Idx]?.level2_label || ''"
                    type="text"
                    placeholder="Label for agents (e.g. Category)"
                    class="w-full px-2 py-1 text-xs rounded border border-woot-200 dark:border-woot-700 bg-white dark:bg-slate-700 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-woot-500"
                    @input="updateEditLevel2Label(editSelectedL1Idx, $event.target.value)"
                  />
                </div>
                <div class="flex-1 overflow-y-auto max-h-52 py-1">
                  <div
                    v-for="(l2opt, l2Idx) in (editState.options[editSelectedL1Idx]?.children || [])"
                    :key="l2Idx"
                    draggable="true"
                    class="group flex items-center gap-1 px-2 py-1.5 cursor-pointer transition-colors"
                    :class="[
                      editSelectedL2Idx === l2Idx ? 'bg-woot-50 dark:bg-woot-900/30' : 'hover:bg-slate-50 dark:hover:bg-slate-700/50',
                      optDragOver.level === 2 && optDragOver.idx === l2Idx ? 'ring-1 ring-woot-400' : '',
                      optDragSrc.level === 2 && optDragSrc.idx === l2Idx ? 'opacity-40' : '',
                    ]"
                    @click="editSelectedL2Idx = l2Idx; editSelectedL3Idx = null"
                    @dragstart="onOptDragStart($event, 2, l2Idx)"
                    @dragover="onOptDragOver($event, 2, l2Idx)"
                    @dragleave="onOptDragLeave"
                    @drop="onEditOptDrop($event, 2, l2Idx)"
                    @dragend="onOptDragEnd"
                  >
                    <span class="i-lucide-grip-vertical size-3 text-slate-400 cursor-grab shrink-0" />
                    <span class="flex-1 text-sm text-slate-700 dark:text-slate-200 truncate">{{ l2opt.label }}</span>
                    <span v-if="l2opt.children?.length" class="text-xs text-woot-400 shrink-0">▸{{ l2opt.children.length }}</span>
                    <span v-if="editSelectedL2Idx === l2Idx" class="text-xs text-woot-500 shrink-0 font-bold">›</span>
                    <button
                      class="p-0.5 text-slate-300 hover:text-red-500 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                      @click.stop="removeEditL2Option(editSelectedL1Idx, l2Idx)"
                    >
                      <span class="i-lucide-x size-3" />
                    </button>
                  </div>
                  <p v-if="!editState.options[editSelectedL1Idx]?.children?.length" class="text-xs text-slate-400 italic text-center py-4">No level 2 choices yet</p>
                </div>
                <div class="border-t border-woot-100 dark:border-woot-800 px-2 py-2 flex gap-1">
                  <input
                    :value="editL2Inputs[editSelectedL1Idx] || ''"
                    type="text"
                    placeholder="Add choice…"
                    class="flex-1 min-w-0 px-2 py-1 text-xs rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-woot-500"
                    @input="editL2Inputs = { ...editL2Inputs, [editSelectedL1Idx]: $event.target.value }"
                    @keydown.enter.prevent="addEditL2Option(editSelectedL1Idx)"
                  />
                  <button
                    class="px-2 py-1 text-xs font-medium text-woot-600 border border-woot-200 rounded hover:bg-woot-50 dark:hover:bg-woot-900/20 shrink-0"
                    @click="addEditL2Option(editSelectedL1Idx)"
                  >+</button>
                </div>
              </div>

              <!-- Column 3: Level 3 choices (shown when L2 is selected) -->
              <div v-if="editSelectedL1Idx !== null && editSelectedL2Idx !== null" class="flex-1 min-w-[185px] flex flex-col rounded-lg border border-purple-200 dark:border-purple-700 bg-white dark:bg-slate-800 overflow-hidden">
                <div class="px-3 py-2 bg-purple-50 dark:bg-purple-900/20 border-b border-purple-200 dark:border-purple-700 space-y-1.5">
                  <p class="text-xs font-semibold text-purple-600 dark:text-purple-400 uppercase tracking-wide">
                    Level 3 — under "{{ editState.options[editSelectedL1Idx]?.children?.[editSelectedL2Idx]?.label }}"
                  </p>
                  <input
                    :value="editState.options[editSelectedL1Idx]?.children?.[editSelectedL2Idx]?.level3_label || ''"
                    type="text"
                    placeholder="Label for agents (e.g. Sub-category)"
                    class="w-full px-2 py-1 text-xs rounded border border-purple-200 dark:border-purple-700 bg-white dark:bg-slate-700 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-woot-500"
                    @input="updateEditLevel3Label(editSelectedL1Idx, editSelectedL2Idx, $event.target.value)"
                  />
                </div>
                <div class="flex-1 overflow-y-auto max-h-52 py-1">
                  <div
                    v-for="(l3opt, l3Idx) in (editState.options[editSelectedL1Idx]?.children?.[editSelectedL2Idx]?.children || [])"
                    :key="l3Idx"
                    draggable="true"
                    class="group flex items-center gap-1 px-2 py-1.5 cursor-pointer transition-colors"
                    :class="[
                      editSelectedL3Idx === l3Idx ? 'bg-purple-50 dark:bg-purple-900/30' : 'hover:bg-slate-50 dark:hover:bg-slate-700/50',
                      optDragOver.level === 3 && optDragOver.idx === l3Idx ? 'ring-1 ring-purple-400' : '',
                      optDragSrc.level === 3 && optDragSrc.idx === l3Idx ? 'opacity-40' : '',
                    ]"
                    @click="editSelectedL3Idx = l3Idx"
                    @dragstart="onOptDragStart($event, 3, l3Idx)"
                    @dragover="onOptDragOver($event, 3, l3Idx)"
                    @dragleave="onOptDragLeave"
                    @drop="onEditOptDrop($event, 3, l3Idx)"
                    @dragend="onOptDragEnd"
                  >
                    <span class="i-lucide-grip-vertical size-3 text-slate-400 cursor-grab shrink-0" />
                    <span class="flex-1 text-sm text-slate-700 dark:text-slate-200 truncate">{{ l3opt.label }}</span>
                    <span v-if="l3opt.children?.length" class="text-xs text-purple-400 shrink-0">▸{{ l3opt.children.length }}</span>
                    <span v-if="editSelectedL3Idx === l3Idx" class="text-xs text-purple-500 shrink-0 font-bold">›</span>
                    <button
                      class="p-0.5 text-slate-300 hover:text-red-500 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                      @click.stop="removeEditL3Option(editSelectedL1Idx, editSelectedL2Idx, l3Idx)"
                    >
                      <span class="i-lucide-x size-3" />
                    </button>
                  </div>
                  <p v-if="!editState.options[editSelectedL1Idx]?.children?.[editSelectedL2Idx]?.children?.length" class="text-xs text-slate-400 italic text-center py-4">No level 3 choices yet</p>
                </div>
                <div class="border-t border-purple-100 dark:border-purple-800 px-2 py-2 flex gap-1">
                  <input
                    :value="editL3Inputs[`${editSelectedL1Idx}_${editSelectedL2Idx}`] || ''"
                    type="text"
                    placeholder="Add choice…"
                    class="flex-1 min-w-0 px-2 py-1 text-xs rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-woot-500"
                    @input="editL3Inputs = { ...editL3Inputs, [`${editSelectedL1Idx}_${editSelectedL2Idx}`]: $event.target.value }"
                    @keydown.enter.prevent="addEditL3Option(editSelectedL1Idx, editSelectedL2Idx)"
                  />
                  <button
                    class="px-2 py-1 text-xs font-medium text-purple-600 border border-purple-200 rounded hover:bg-purple-50 dark:hover:bg-purple-900/20 shrink-0"
                    @click="addEditL3Option(editSelectedL1Idx, editSelectedL2Idx)"
                  >+</button>
                </div>
              </div>

              <!-- Column 4: Level 4 choices (shown when L3 is selected) -->
              <div v-if="editSelectedL1Idx !== null && editSelectedL2Idx !== null && editSelectedL3Idx !== null" class="flex-1 min-w-[185px] flex flex-col rounded-lg border border-pink-200 dark:border-pink-700 bg-white dark:bg-slate-800 overflow-hidden">
                <div class="px-3 py-2 bg-pink-50 dark:bg-pink-900/20 border-b border-pink-200 dark:border-pink-700 space-y-1.5">
                  <p class="text-xs font-semibold text-pink-600 dark:text-pink-400 uppercase tracking-wide">
                    Level 4 — under "{{ editState.options[editSelectedL1Idx]?.children?.[editSelectedL2Idx]?.children?.[editSelectedL3Idx]?.label }}"
                  </p>
                  <input
                    :value="editState.options[editSelectedL1Idx]?.children?.[editSelectedL2Idx]?.children?.[editSelectedL3Idx]?.level4_label || ''"
                    type="text"
                    placeholder="Label for agents (e.g. Request Name)"
                    class="w-full px-2 py-1 text-xs rounded border border-pink-200 dark:border-pink-700 bg-white dark:bg-slate-700 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-pink-500"
                    @input="updateEditLevel4Label(editSelectedL1Idx, editSelectedL2Idx, editSelectedL3Idx, $event.target.value)"
                  />
                </div>
                <div class="flex-1 overflow-y-auto max-h-52 py-1">
                  <div
                    v-for="(l4opt, l4Idx) in (editState.options[editSelectedL1Idx]?.children?.[editSelectedL2Idx]?.children?.[editSelectedL3Idx]?.children || [])"
                    :key="l4Idx"
                    draggable="true"
                    class="group flex items-center gap-1 px-2 py-1.5 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
                    :class="[
                      optDragOver.level === 4 && optDragOver.idx === l4Idx ? 'ring-1 ring-pink-400' : '',
                      optDragSrc.level === 4 && optDragSrc.idx === l4Idx ? 'opacity-40' : '',
                    ]"
                    @dragstart="onOptDragStart($event, 4, l4Idx)"
                    @dragover="onOptDragOver($event, 4, l4Idx)"
                    @dragleave="onOptDragLeave"
                    @drop="onEditOptDrop($event, 4, l4Idx)"
                    @dragend="onOptDragEnd"
                  >
                    <span class="i-lucide-grip-vertical size-3 text-slate-400 cursor-grab shrink-0" />
                    <span class="flex-1 text-sm text-slate-700 dark:text-slate-200 truncate">{{ l4opt.label }}</span>
                    <button
                      class="p-0.5 text-slate-300 hover:text-red-500 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                      @click="removeEditL4Option(editSelectedL1Idx, editSelectedL2Idx, editSelectedL3Idx, l4Idx)"
                    >
                      <span class="i-lucide-x size-3" />
                    </button>
                  </div>
                  <p v-if="!editState.options[editSelectedL1Idx]?.children?.[editSelectedL2Idx]?.children?.[editSelectedL3Idx]?.children?.length" class="text-xs text-slate-400 italic text-center py-4">No level 4 choices yet</p>
                </div>
                <div class="border-t border-pink-100 dark:border-pink-800 px-2 py-2 flex gap-1">
                  <input
                    :value="editL4Inputs[`${editSelectedL1Idx}_${editSelectedL2Idx}_${editSelectedL3Idx}`] || ''"
                    type="text"
                    placeholder="Add choice…"
                    class="flex-1 min-w-0 px-2 py-1 text-xs rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-pink-500"
                    @input="editL4Inputs = { ...editL4Inputs, [`${editSelectedL1Idx}_${editSelectedL2Idx}_${editSelectedL3Idx}`]: $event.target.value }"
                    @keydown.enter.prevent="addEditL4Option(editSelectedL1Idx, editSelectedL2Idx, editSelectedL3Idx)"
                  />
                  <button
                    class="px-2 py-1 text-xs font-medium text-pink-600 border border-pink-200 rounded hover:bg-pink-50 dark:hover:bg-pink-900/20 shrink-0"
                    @click="addEditL4Option(editSelectedL1Idx, editSelectedL2Idx, editSelectedL3Idx)"
                  >+</button>
                </div>
              </div>

            </div>
          </div>

          <!-- Conditional Sections (visible only for system dropdown fields like Type) -->
          <div v-if="field.is_system && field.field_type === 'dropdown'" class="rounded-lg border border-slate-200 dark:border-slate-600 overflow-hidden">
            <div class="px-3 py-2 bg-slate-50 dark:bg-slate-700 border-b border-slate-200 dark:border-slate-600 flex items-center justify-between">
              <div>
                <p class="text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wide">Conditional Sections</p>
                <p class="text-xs text-slate-400 mt-0.5">Fields that appear only when a specific option is selected</p>
              </div>
            </div>
            <div class="divide-y divide-slate-100 dark:divide-slate-700">
              <div
                v-for="opt in editState.options"
                :key="opt.value"
                class="px-3 py-2.5"
              >
                <div class="flex items-center justify-between mb-1.5">
                  <p class="text-xs font-semibold text-slate-600 dark:text-slate-300">
                    <span class="inline-block w-2 h-2 rounded-full bg-slate-300 dark:bg-slate-500 mr-1.5" />
                    {{ opt.label }}
                  </p>
                  <button
                    class="text-xs px-2 py-0.5 rounded border border-woot-300 text-woot-600 hover:bg-woot-50 dark:hover:bg-woot-900/20 flex items-center gap-1"
                    @click="cancelEdit(); openAddFieldWithCondition(field.field_key, opt.value)"
                  >
                    <span class="i-lucide-plus size-3" />
                    Add section
                  </button>
                </div>
                <div v-if="sectionsByFieldAndValue[field.field_key]?.[opt.value]?.length" class="space-y-1 pl-3.5">
                  <div
                    v-for="sf in sectionsByFieldAndValue[field.field_key][opt.value]"
                    :key="sf.id"
                    draggable="true"
                    class="flex items-center gap-2 text-xs rounded px-1 cursor-move transition-colors"
                    :class="[
                      condDragOverId === sf.id ? 'bg-woot-50 dark:bg-woot-900/20 ring-1 ring-woot-300' : '',
                      condDragSrcId === sf.id ? 'opacity-40' : '',
                    ]"
                    @dragstart="onCondDragStart($event, sf)"
                    @dragover="onCondDragOver($event, sf)"
                    @dragleave="onCondDragLeave"
                    @drop="onCondDrop($event, sf, sectionsByFieldAndValue[field.field_key][opt.value])"
                    @dragend="onCondDragEnd"
                  >
                    <span class="i-lucide-grip-vertical size-3 text-slate-400 cursor-grab shrink-0" />
                    <span class="text-slate-700 dark:text-slate-300 font-medium">{{ sf.label }}</span>
                    <span class="text-slate-400">({{ sf.field_type }})</span>
                    <span v-if="!sf.enabled" class="text-amber-500">[hidden]</span>
                    <button
                      class="ml-auto text-slate-400 hover:text-woot-500"
                      title="Edit this field"
                      @click.stop="cancelEdit(); startEdit(sf)"
                    >
                      <span class="i-lucide-pencil size-3" />
                    </button>
                  </div>
                </div>
                <p v-else class="pl-3.5 text-xs text-slate-400 italic">No sections — click "Add section" to add one</p>
              </div>
            </div>

            <!-- Orphaned conditional fields: their parent option was removed/renamed -->
            <div v-if="orphanedSectionsFor(field).length" class="border-t border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 px-3 py-2.5">
              <p class="text-xs font-semibold text-amber-600 dark:text-amber-400 mb-1">Unmatched Conditions</p>
              <p class="text-xs text-amber-500 dark:text-amber-500 mb-2">These fields have a condition that no longer matches any Type option. Edit to reassign or delete them.</p>
              <div class="space-y-1">
                <div
                  v-for="sf in orphanedSectionsFor(field)"
                  :key="sf.id"
                  class="flex items-center gap-2 text-xs"
                >
                  <span class="i-lucide-alert-triangle size-3 text-amber-500" />
                  <span class="text-slate-700 dark:text-slate-300 font-medium">{{ sf.label }}</span>
                  <span class="text-slate-400">(when Type = {{ sf.conditions.show_when.value }})</span>
                  <button
                    class="ml-auto text-slate-400 hover:text-woot-500"
                    title="Edit this field"
                    @click="cancelEdit(); startEdit(sf)"
                  >
                    <span class="i-lucide-pencil size-3" />
                  </button>
                  <button
                    class="text-slate-400 hover:text-red-500"
                    title="Delete this field"
                    @click="deleteField(sf)"
                  >
                    <span class="i-lucide-trash-2 size-3" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="flex gap-2 pt-1">
            <button
              class="px-4 py-1.5 text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
              @click="cancelEdit"
            >Cancel</button>
            <button
              :disabled="uiFlags.isSaving"
              class="px-4 py-1.5 text-sm font-medium text-white bg-woot-500 rounded-lg hover:bg-woot-600 disabled:opacity-50"
              @click="saveEdit(field.id)"
            >{{ uiFlags.isSaving ? 'Saving…' : 'Save' }}</button>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Add new field modal ── -->
    <Dialog
      ref="addFieldDialogRef"
      title="Add New Field"
      width="3xl"
      :overflow-y-auto="true"
      :confirm-button-label="uiFlags.isSaving ? 'Saving…' : 'Add Field'"
      cancel-button-label="Cancel"
      :disable-confirm-button="!newField.label?.trim() || uiFlags.isSaving"
      :is-loading="uiFlags.isSaving"
      @confirm="saveNewField"
      @close="showAddField = false; addFieldPrefillCondition = null"
    >
      <template v-if="addFieldPrefillCondition" #description>
        <p class="text-xs text-woot-600 dark:text-woot-400 mt-1 flex items-center gap-1.5">
          <span class="i-lucide-link size-3" />
          This field will appear only when Type is "{{ addFieldPrefillCondition.value }}"
        </p>
      </template>

      <div class="space-y-4">
        <div>
          <label class="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Field Label</label>
          <input
            v-model="newField.label"
            type="text"
            placeholder="e.g. Department"
            class="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-woot-500"
          />
        </div>

        <div>
          <label class="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Field Type</label>
          <select
            v-model="newField.field_type"
            class="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-woot-500"
          >
            <option value="text">Text</option>
            <option value="dropdown">Dropdown</option>
            <option value="date">Date</option>
            <option value="number">Number</option>
          </select>
        </div>

        <!-- Visibility Condition (new field) -->
        <div class="rounded-lg border border-slate-200 dark:border-slate-600 p-3 space-y-2">
          <p class="text-xs font-semibold text-slate-500 uppercase tracking-wide">Visibility Condition</p>
          <!-- Locked when coming from "Add section" shortcut -->
          <div v-if="addFieldPrefillCondition" class="flex items-center gap-2 text-xs text-woot-600 dark:text-woot-400">
            <span class="i-lucide-lock size-3.5" />
            Show only when Type is <strong class="ml-1">{{ ticketTypeOptions.find(o => o.value === addFieldPrefillCondition.value)?.label || addFieldPrefillCondition.value }}</strong>
          </div>
          <template v-else>
            <div class="flex items-center gap-2">
              <button
                class="relative inline-flex h-5 w-9 items-center rounded-full transition-colors shrink-0"
                :class="newField.conditions?.show_when ? 'bg-woot-500' : 'bg-slate-300 dark:bg-slate-600'"
                @click="newField.conditions = newField.conditions?.show_when ? {} : { show_when: { field_key: 'ticket_type', value: ticketTypeOptions[0]?.value || '' } }"
              >
                <span class="inline-block size-4 rounded-full bg-white shadow transition-transform" :class="newField.conditions?.show_when ? 'translate-x-4' : 'translate-x-0.5'" />
              </button>
              <span class="text-xs text-slate-500">Only show when another field matches</span>
            </div>
            <div v-if="newField.conditions?.show_when" class="flex items-center gap-2 pl-1">
              <span class="text-xs text-slate-500">Show when Type is</span>
              <select
                :value="newField.conditions.show_when.value"
                class="px-2 py-1 text-xs rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-woot-500"
                @change="newField.conditions = { show_when: { field_key: 'ticket_type', value: $event.target.value } }"
              >
                <option v-for="opt in ticketTypeOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
              </select>
            </div>
          </template>
        </div>

        <!-- Dropdown options — cascaded layout -->
        <div v-if="newField.field_type === 'dropdown'">
          <label class="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Dropdown Options</label>
          <p class="text-xs text-slate-400 mb-3">
            The <strong class="text-slate-500">Field Label</strong> above is shown as a non-selectable header in Ticket Properties.
            Add choices here — selecting a choice reveals the next level. Click any choice to add sub-choices and set each level's label.
          </p>

          <div class="flex gap-3 overflow-x-auto pb-1">

            <!-- Column 1: Level 1 choices (shown under the field label) -->
            <div class="flex-1 min-w-[170px] flex flex-col rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 overflow-hidden">
              <div class="px-3 py-2 bg-white dark:bg-slate-700 border-b border-slate-200 dark:border-slate-600">
                <p class="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Choices under "{{ newField.label || 'Field Label' }}"</p>
              </div>
              <div class="flex-1 overflow-y-auto max-h-52 py-1">
                <div
                  v-for="(opt, idx) in newField.options"
                  :key="idx"
                  draggable="true"
                  class="group flex items-center gap-1 px-2 py-1.5 cursor-pointer transition-colors"
                  :class="[
                    newSelectedL1Idx === idx ? 'bg-woot-50 dark:bg-woot-900/30' : 'hover:bg-slate-100 dark:hover:bg-slate-700/50',
                    optDragOver.level === 1 && optDragOver.idx === idx ? 'ring-1 ring-woot-400' : '',
                    optDragSrc.level === 1 && optDragSrc.idx === idx ? 'opacity-40' : '',
                  ]"
                  @click="newSelectedL1Idx = idx; newSelectedL2Idx = null; newSelectedL3Idx = null"
                  @dragstart="onOptDragStart($event, 1, idx)"
                  @dragover="onOptDragOver($event, 1, idx)"
                  @dragleave="onOptDragLeave"
                  @drop="onNewOptDrop($event, 1, idx)"
                  @dragend="onOptDragEnd"
                >
                  <span class="i-lucide-grip-vertical size-3 text-slate-400 cursor-grab shrink-0" />
                  <span class="flex-1 text-sm text-slate-700 dark:text-slate-200 truncate">{{ opt.label }}</span>
                  <span v-if="opt.children?.length" class="text-xs text-woot-400 shrink-0">▸{{ opt.children.length }}</span>
                  <span v-if="newSelectedL1Idx === idx" class="text-xs text-woot-500 shrink-0 font-bold">›</span>
                  <button
                    class="p-0.5 text-slate-300 hover:text-red-500 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                    @click.stop="removeNewOption(idx)"
                  >
                    <span class="i-lucide-x size-3" />
                  </button>
                </div>
                <p v-if="!newField.options.length" class="text-xs text-slate-400 italic text-center py-4">No choices yet</p>
              </div>
              <div class="border-t border-slate-200 dark:border-slate-600 px-2 py-2 flex gap-1">
                <input
                  v-model="newOptionLabel"
                  type="text"
                  placeholder="Add choice…"
                  class="flex-1 min-w-0 px-2 py-1 text-xs rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-woot-500"
                  @keydown.enter.prevent="addNewOption"
                />
                <button
                  class="px-2 py-1 text-xs font-medium text-woot-600 border border-woot-200 rounded hover:bg-woot-50 shrink-0"
                  @click="addNewOption"
                >+</button>
              </div>
            </div>

            <!-- Column 2: Level 2 choices -->
            <div v-if="newSelectedL1Idx !== null" class="flex-1 min-w-[185px] flex flex-col rounded-lg border border-woot-200 dark:border-woot-700 bg-white dark:bg-slate-800 overflow-hidden">
              <div class="px-3 py-2 bg-woot-50 dark:bg-woot-900/20 border-b border-woot-200 dark:border-woot-700 space-y-1.5">
                <p class="text-xs font-semibold text-woot-600 dark:text-woot-400 uppercase tracking-wide">
                  Level 2 — under "{{ newField.options[newSelectedL1Idx]?.label }}"
                </p>
                <input
                  :value="newField.options[newSelectedL1Idx]?.level2_label || ''"
                  type="text"
                  placeholder="Label for agents (e.g. Category)"
                  class="w-full px-2 py-1 text-xs rounded border border-woot-200 dark:border-woot-700 bg-white dark:bg-slate-700 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-woot-500"
                  @input="updateNewLevel2Label(newSelectedL1Idx, $event.target.value)"
                />
              </div>
              <div class="flex-1 overflow-y-auto max-h-52 py-1">
                <div
                  v-for="(l2opt, l2Idx) in (newField.options[newSelectedL1Idx]?.children || [])"
                  :key="l2Idx"
                  draggable="true"
                  class="group flex items-center gap-1 px-2 py-1.5 cursor-pointer transition-colors"
                  :class="[
                    newSelectedL2Idx === l2Idx ? 'bg-woot-50 dark:bg-woot-900/30' : 'hover:bg-slate-50 dark:hover:bg-slate-700/50',
                    optDragOver.level === 2 && optDragOver.idx === l2Idx ? 'ring-1 ring-woot-400' : '',
                    optDragSrc.level === 2 && optDragSrc.idx === l2Idx ? 'opacity-40' : '',
                  ]"
                  @click="newSelectedL2Idx = l2Idx; newSelectedL3Idx = null"
                  @dragstart="onOptDragStart($event, 2, l2Idx)"
                  @dragover="onOptDragOver($event, 2, l2Idx)"
                  @dragleave="onOptDragLeave"
                  @drop="onNewOptDrop($event, 2, l2Idx)"
                  @dragend="onOptDragEnd"
                >
                  <span class="i-lucide-grip-vertical size-3 text-slate-400 cursor-grab shrink-0" />
                  <span class="flex-1 text-sm text-slate-700 dark:text-slate-200 truncate">{{ l2opt.label }}</span>
                  <span v-if="l2opt.children?.length" class="text-xs text-woot-400 shrink-0">▸{{ l2opt.children.length }}</span>
                  <span v-if="newSelectedL2Idx === l2Idx" class="text-xs text-woot-500 shrink-0 font-bold">›</span>
                  <button
                    class="p-0.5 text-slate-300 hover:text-red-500 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                    @click.stop="removeNewL2Option(newSelectedL1Idx, l2Idx)"
                  >
                    <span class="i-lucide-x size-3" />
                  </button>
                </div>
                <p v-if="!newField.options[newSelectedL1Idx]?.children?.length" class="text-xs text-slate-400 italic text-center py-4">No level 2 choices yet</p>
              </div>
              <div class="border-t border-woot-100 dark:border-woot-800 px-2 py-2 flex gap-1">
                <input
                  :value="newL2Inputs[newSelectedL1Idx] || ''"
                  type="text"
                  placeholder="Add choice…"
                  class="flex-1 min-w-0 px-2 py-1 text-xs rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-woot-500"
                  @input="newL2Inputs = { ...newL2Inputs, [newSelectedL1Idx]: $event.target.value }"
                  @keydown.enter.prevent="addNewL2Option(newSelectedL1Idx)"
                />
                <button
                  class="px-2 py-1 text-xs font-medium text-woot-600 border border-woot-200 rounded hover:bg-woot-50 shrink-0"
                  @click="addNewL2Option(newSelectedL1Idx)"
                >+</button>
              </div>
            </div>

            <!-- Column 3: Level 3 choices -->
            <div v-if="newSelectedL1Idx !== null && newSelectedL2Idx !== null" class="flex-1 min-w-[185px] flex flex-col rounded-lg border border-purple-200 dark:border-purple-700 bg-white dark:bg-slate-800 overflow-hidden">
              <div class="px-3 py-2 bg-purple-50 dark:bg-purple-900/20 border-b border-purple-200 dark:border-purple-700 space-y-1.5">
                <p class="text-xs font-semibold text-purple-600 dark:text-purple-400 uppercase tracking-wide">
                  Level 3 — under "{{ newField.options[newSelectedL1Idx]?.children?.[newSelectedL2Idx]?.label }}"
                </p>
                <input
                  :value="newField.options[newSelectedL1Idx]?.children?.[newSelectedL2Idx]?.level3_label || ''"
                  type="text"
                  placeholder="Label for agents (e.g. Sub-category)"
                  class="w-full px-2 py-1 text-xs rounded border border-purple-200 dark:border-purple-700 bg-white dark:bg-slate-700 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-woot-500"
                  @input="updateNewLevel3Label(newSelectedL1Idx, newSelectedL2Idx, $event.target.value)"
                />
              </div>
              <div class="flex-1 overflow-y-auto max-h-52 py-1">
                <div
                  v-for="(l3opt, l3Idx) in (newField.options[newSelectedL1Idx]?.children?.[newSelectedL2Idx]?.children || [])"
                  :key="l3Idx"
                  draggable="true"
                  class="group flex items-center gap-1 px-2 py-1.5 cursor-pointer transition-colors"
                  :class="[
                    newSelectedL3Idx === l3Idx ? 'bg-purple-50 dark:bg-purple-900/30' : 'hover:bg-slate-50 dark:hover:bg-slate-700/50',
                    optDragOver.level === 3 && optDragOver.idx === l3Idx ? 'ring-1 ring-purple-400' : '',
                    optDragSrc.level === 3 && optDragSrc.idx === l3Idx ? 'opacity-40' : '',
                  ]"
                  @click="newSelectedL3Idx = l3Idx"
                  @dragstart="onOptDragStart($event, 3, l3Idx)"
                  @dragover="onOptDragOver($event, 3, l3Idx)"
                  @dragleave="onOptDragLeave"
                  @drop="onNewOptDrop($event, 3, l3Idx)"
                  @dragend="onOptDragEnd"
                >
                  <span class="i-lucide-grip-vertical size-3 text-slate-400 cursor-grab shrink-0" />
                  <span class="flex-1 text-sm text-slate-700 dark:text-slate-200 truncate">{{ l3opt.label }}</span>
                  <span v-if="l3opt.children?.length" class="text-xs text-purple-400 shrink-0">▸{{ l3opt.children.length }}</span>
                  <span v-if="newSelectedL3Idx === l3Idx" class="text-xs text-purple-500 shrink-0 font-bold">›</span>
                  <button
                    class="p-0.5 text-slate-300 hover:text-red-500 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                    @click.stop="removeNewL3Option(newSelectedL1Idx, newSelectedL2Idx, l3Idx)"
                  >
                    <span class="i-lucide-x size-3" />
                  </button>
                </div>
                <p v-if="!newField.options[newSelectedL1Idx]?.children?.[newSelectedL2Idx]?.children?.length" class="text-xs text-slate-400 italic text-center py-4">No level 3 choices yet</p>
              </div>
              <div class="border-t border-purple-100 dark:border-purple-800 px-2 py-2 flex gap-1">
                <input
                  :value="newL3Inputs[`${newSelectedL1Idx}_${newSelectedL2Idx}`] || ''"
                  type="text"
                  placeholder="Add choice…"
                  class="flex-1 min-w-0 px-2 py-1 text-xs rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-woot-500"
                  @input="newL3Inputs = { ...newL3Inputs, [`${newSelectedL1Idx}_${newSelectedL2Idx}`]: $event.target.value }"
                  @keydown.enter.prevent="addNewL3Option(newSelectedL1Idx, newSelectedL2Idx)"
                />
                <button
                  class="px-2 py-1 text-xs font-medium text-purple-600 border border-purple-200 rounded hover:bg-purple-50 shrink-0"
                  @click="addNewL3Option(newSelectedL1Idx, newSelectedL2Idx)"
                >+</button>
              </div>
            </div>

            <!-- Column 4: Level 4 choices (shown when L3 is selected) -->
            <div v-if="newSelectedL1Idx !== null && newSelectedL2Idx !== null && newSelectedL3Idx !== null" class="flex-1 min-w-[185px] flex flex-col rounded-lg border border-pink-200 dark:border-pink-700 bg-white dark:bg-slate-800 overflow-hidden">
              <div class="px-3 py-2 bg-pink-50 dark:bg-pink-900/20 border-b border-pink-200 dark:border-pink-700 space-y-1.5">
                <p class="text-xs font-semibold text-pink-600 dark:text-pink-400 uppercase tracking-wide">
                  Level 4 — under "{{ newField.options[newSelectedL1Idx]?.children?.[newSelectedL2Idx]?.children?.[newSelectedL3Idx]?.label }}"
                </p>
                <input
                  :value="newField.options[newSelectedL1Idx]?.children?.[newSelectedL2Idx]?.children?.[newSelectedL3Idx]?.level4_label || ''"
                  type="text"
                  placeholder="Label for agents (e.g. Request Name)"
                  class="w-full px-2 py-1 text-xs rounded border border-pink-200 dark:border-pink-700 bg-white dark:bg-slate-700 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-pink-500"
                  @input="updateNewLevel4Label(newSelectedL1Idx, newSelectedL2Idx, newSelectedL3Idx, $event.target.value)"
                />
              </div>
              <div class="flex-1 overflow-y-auto max-h-52 py-1">
                <div
                  v-for="(l4opt, l4Idx) in (newField.options[newSelectedL1Idx]?.children?.[newSelectedL2Idx]?.children?.[newSelectedL3Idx]?.children || [])"
                  :key="l4Idx"
                  draggable="true"
                  class="group flex items-center gap-1 px-2 py-1.5 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
                  :class="[
                    optDragOver.level === 4 && optDragOver.idx === l4Idx ? 'ring-1 ring-pink-400' : '',
                    optDragSrc.level === 4 && optDragSrc.idx === l4Idx ? 'opacity-40' : '',
                  ]"
                  @dragstart="onOptDragStart($event, 4, l4Idx)"
                  @dragover="onOptDragOver($event, 4, l4Idx)"
                  @dragleave="onOptDragLeave"
                  @drop="onNewOptDrop($event, 4, l4Idx)"
                  @dragend="onOptDragEnd"
                >
                  <span class="i-lucide-grip-vertical size-3 text-slate-400 cursor-grab shrink-0" />
                  <span class="flex-1 text-sm text-slate-700 dark:text-slate-200 truncate">{{ l4opt.label }}</span>
                  <button
                    class="p-0.5 text-slate-300 hover:text-red-500 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                    @click="removeNewL4Option(newSelectedL1Idx, newSelectedL2Idx, newSelectedL3Idx, l4Idx)"
                  >
                    <span class="i-lucide-x size-3" />
                  </button>
                </div>
                <p v-if="!newField.options[newSelectedL1Idx]?.children?.[newSelectedL2Idx]?.children?.[newSelectedL3Idx]?.children?.length" class="text-xs text-slate-400 italic text-center py-4">No level 4 choices yet</p>
              </div>
              <div class="border-t border-pink-100 dark:border-pink-800 px-2 py-2 flex gap-1">
                <input
                  :value="newL4Inputs[`${newSelectedL1Idx}_${newSelectedL2Idx}_${newSelectedL3Idx}`] || ''"
                  type="text"
                  placeholder="Add choice…"
                  class="flex-1 min-w-0 px-2 py-1 text-xs rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-pink-500"
                  @input="newL4Inputs = { ...newL4Inputs, [`${newSelectedL1Idx}_${newSelectedL2Idx}_${newSelectedL3Idx}`]: $event.target.value }"
                  @keydown.enter.prevent="addNewL4Option(newSelectedL1Idx, newSelectedL2Idx, newSelectedL3Idx)"
                />
                <button
                  class="px-2 py-1 text-xs font-medium text-pink-600 border border-pink-200 rounded hover:bg-pink-50 shrink-0"
                  @click="addNewL4Option(newSelectedL1Idx, newSelectedL2Idx, newSelectedL3Idx)"
                >+</button>
              </div>
            </div>

          </div>
        </div>
      </div>
    </Dialog>

    <!-- ── Edit conditional field modal ── -->
    <Dialog
      ref="editFieldDialogRef"
      title="Edit Field"
      width="3xl"
      :overflow-y-auto="true"
      :confirm-button-label="uiFlags.isSaving ? 'Saving…' : 'Save'"
      cancel-button-label="Cancel"
      :disable-confirm-button="uiFlags.isSaving"
      :is-loading="uiFlags.isSaving"
      @confirm="saveEdit(editingConditionalField?.id)"
      @close="cancelEdit"
    >
      <template v-if="editingConditionalField" #description>
        <p class="text-xs text-amber-600 dark:text-amber-400 mt-0.5 flex items-center gap-1.5">
          <span class="i-lucide-link size-3" />
          Shown only when Type is "{{ editingConditionalField.conditions.show_when.value }}"
        </p>
      </template>

      <div v-if="editingConditionalField" class="space-y-4">
        <!-- Label -->
        <div>
          <label class="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Field Label</label>
          <input
            v-model="editState.label"
            type="text"
            class="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-woot-500"
          />
        </div>

        <!-- Enabled toggle -->
        <div class="flex items-center gap-3">
          <label class="text-xs font-semibold text-slate-500 uppercase tracking-wide">Visible in Ticket Properties</label>
          <button
            class="relative inline-flex h-5 w-9 items-center rounded-full transition-colors"
            :class="editState.enabled ? 'bg-woot-500' : 'bg-slate-300 dark:bg-slate-600'"
            @click="editState.enabled = !editState.enabled"
          >
            <span
              class="inline-block size-4 rounded-full bg-white shadow transition-transform"
              :class="editState.enabled ? 'translate-x-4' : 'translate-x-0.5'"
            />
          </button>
        </div>

        <!-- Dropdown options (if applicable) -->
        <div v-if="editingConditionalField.field_type === 'dropdown'">
          <label class="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Dropdown Options</label>
          <p class="text-xs text-slate-400 mb-3">
            The <strong class="text-slate-500">Field Label</strong> above is shown as a non-selectable header in Ticket Properties.
            Add choices here — when an agent selects a choice, the next level appears below it.
            Click any choice to configure its sub-choices and set each level's label.
          </p>

          <div class="flex gap-3 overflow-x-auto pb-1">
            <!-- Column 1: Level 1 choices -->
            <div class="flex-1 min-w-[170px] flex flex-col rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 overflow-hidden">
              <div class="px-3 py-2 bg-slate-50 dark:bg-slate-700 border-b border-slate-200 dark:border-slate-600">
                <p class="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Choices under "{{ editState.label || 'Field Label' }}"</p>
              </div>
              <div class="flex-1 overflow-y-auto max-h-52 py-1">
                <div
                  v-for="(opt, idx) in editState.options"
                  :key="idx"
                  draggable="true"
                  class="group flex items-center gap-1 px-2 py-1.5 cursor-pointer transition-colors"
                  :class="[
                    editSelectedL1Idx === idx ? 'bg-woot-50 dark:bg-woot-900/30' : 'hover:bg-slate-50 dark:hover:bg-slate-700/50',
                    optDragOver.level === 1 && optDragOver.idx === idx ? 'ring-1 ring-woot-400' : '',
                    optDragSrc.level === 1 && optDragSrc.idx === idx ? 'opacity-40' : '',
                  ]"
                  @click="editSelectedL1Idx = idx; editSelectedL2Idx = null; editSelectedL3Idx = null"
                  @dragstart="onOptDragStart($event, 1, idx)"
                  @dragover="onOptDragOver($event, 1, idx)"
                  @dragleave="onOptDragLeave"
                  @drop="onEditOptDrop($event, 1, idx)"
                  @dragend="onOptDragEnd"
                >
                  <span class="i-lucide-grip-vertical size-3 text-slate-400 cursor-grab shrink-0" />
                  <span class="flex-1 min-w-0 text-sm text-slate-700 dark:text-slate-200 truncate">{{ opt.label }}</span>
                  <span v-if="opt.children?.length" class="text-xs text-woot-400 shrink-0">▸{{ opt.children.length }}</span>
                  <span v-if="editSelectedL1Idx === idx" class="text-xs text-woot-500 shrink-0 font-bold">›</span>
                  <button class="p-0.5 text-slate-300 hover:text-red-500 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" @click.stop="removeEditOption(idx)">
                    <span class="i-lucide-x size-3" />
                  </button>
                </div>
                <p v-if="!editState.options.length" class="text-xs text-slate-400 italic text-center py-4">No choices yet</p>
              </div>
              <div class="border-t border-slate-200 dark:border-slate-600 px-2 py-2 flex gap-1">
                <input v-model="editOptionInput" type="text" placeholder="Add choice…" class="flex-1 min-w-0 px-2 py-1 text-xs rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-woot-500" @keydown.enter.prevent="addEditOption" />
                <button class="px-2 py-1 text-xs font-medium text-woot-600 border border-woot-200 rounded hover:bg-woot-50 dark:hover:bg-woot-900/20 shrink-0" @click="addEditOption">+</button>
              </div>
            </div>

            <!-- Column 2: Level 2 choices -->
            <div v-if="editSelectedL1Idx !== null" class="flex-1 min-w-[185px] flex flex-col rounded-lg border border-woot-200 dark:border-woot-700 bg-white dark:bg-slate-800 overflow-hidden">
              <div class="px-3 py-2 bg-woot-50 dark:bg-woot-900/20 border-b border-woot-200 dark:border-woot-700 space-y-1.5">
                <p class="text-xs font-semibold text-woot-600 dark:text-woot-400 uppercase tracking-wide">Level 2 — under "{{ editState.options[editSelectedL1Idx]?.label }}"</p>
                <input :value="editState.options[editSelectedL1Idx]?.level2_label || ''" type="text" placeholder="Label for agents (e.g. Category)" class="w-full px-2 py-1 text-xs rounded border border-woot-200 dark:border-woot-700 bg-white dark:bg-slate-700 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-woot-500" @input="updateEditLevel2Label(editSelectedL1Idx, $event.target.value)" />
              </div>
              <div class="flex-1 overflow-y-auto max-h-52 py-1">
                <div
                  v-for="(l2opt, l2Idx) in (editState.options[editSelectedL1Idx]?.children || [])"
                  :key="l2Idx"
                  draggable="true"
                  class="group flex items-center gap-1 px-2 py-1.5 cursor-pointer transition-colors"
                  :class="[
                    editSelectedL2Idx === l2Idx ? 'bg-woot-50 dark:bg-woot-900/30' : 'hover:bg-slate-50 dark:hover:bg-slate-700/50',
                    optDragOver.level === 2 && optDragOver.idx === l2Idx ? 'ring-1 ring-woot-400' : '',
                    optDragSrc.level === 2 && optDragSrc.idx === l2Idx ? 'opacity-40' : '',
                  ]"
                  @click="editSelectedL2Idx = l2Idx; editSelectedL3Idx = null"
                  @dragstart="onOptDragStart($event, 2, l2Idx)"
                  @dragover="onOptDragOver($event, 2, l2Idx)"
                  @dragleave="onOptDragLeave"
                  @drop="onEditOptDrop($event, 2, l2Idx)"
                  @dragend="onOptDragEnd"
                >
                  <span class="i-lucide-grip-vertical size-3 text-slate-400 cursor-grab shrink-0" />
                  <span class="flex-1 text-sm text-slate-700 dark:text-slate-200 truncate">{{ l2opt.label }}</span>
                  <span v-if="l2opt.children?.length" class="text-xs text-woot-400 shrink-0">▸{{ l2opt.children.length }}</span>
                  <span v-if="editSelectedL2Idx === l2Idx" class="text-xs text-woot-500 shrink-0 font-bold">›</span>
                  <button class="p-0.5 text-slate-300 hover:text-red-500 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" @click.stop="removeEditL2Option(editSelectedL1Idx, l2Idx)"><span class="i-lucide-x size-3" /></button>
                </div>
                <p v-if="!editState.options[editSelectedL1Idx]?.children?.length" class="text-xs text-slate-400 italic text-center py-4">No level 2 choices yet</p>
              </div>
              <div class="border-t border-woot-100 dark:border-woot-800 px-2 py-2 flex gap-1">
                <input :value="editL2Inputs[editSelectedL1Idx] || ''" type="text" placeholder="Add choice…" class="flex-1 min-w-0 px-2 py-1 text-xs rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-woot-500" @input="editL2Inputs = { ...editL2Inputs, [editSelectedL1Idx]: $event.target.value }" @keydown.enter.prevent="addEditL2Option(editSelectedL1Idx)" />
                <button class="px-2 py-1 text-xs font-medium text-woot-600 border border-woot-200 rounded hover:bg-woot-50 dark:hover:bg-woot-900/20 shrink-0" @click="addEditL2Option(editSelectedL1Idx)">+</button>
              </div>
            </div>

            <!-- Column 3: Level 3 choices -->
            <div v-if="editSelectedL1Idx !== null && editSelectedL2Idx !== null" class="flex-1 min-w-[185px] flex flex-col rounded-lg border border-purple-200 dark:border-purple-700 bg-white dark:bg-slate-800 overflow-hidden">
              <div class="px-3 py-2 bg-purple-50 dark:bg-purple-900/20 border-b border-purple-200 dark:border-purple-700 space-y-1.5">
                <p class="text-xs font-semibold text-purple-600 dark:text-purple-400 uppercase tracking-wide">Level 3 — under "{{ editState.options[editSelectedL1Idx]?.children?.[editSelectedL2Idx]?.label }}"</p>
                <input :value="editState.options[editSelectedL1Idx]?.children?.[editSelectedL2Idx]?.level3_label || ''" type="text" placeholder="Label for agents (e.g. Sub-category)" class="w-full px-2 py-1 text-xs rounded border border-purple-200 dark:border-purple-700 bg-white dark:bg-slate-700 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-woot-500" @input="updateEditLevel3Label(editSelectedL1Idx, editSelectedL2Idx, $event.target.value)" />
              </div>
              <div class="flex-1 overflow-y-auto max-h-52 py-1">
                <div
                  v-for="(l3opt, l3Idx) in (editState.options[editSelectedL1Idx]?.children?.[editSelectedL2Idx]?.children || [])"
                  :key="l3Idx"
                  draggable="true"
                  class="group flex items-center gap-1 px-2 py-1.5 cursor-pointer transition-colors"
                  :class="[
                    editSelectedL3Idx === l3Idx ? 'bg-purple-50 dark:bg-purple-900/30' : 'hover:bg-slate-50 dark:hover:bg-slate-700/50',
                    optDragOver.level === 3 && optDragOver.idx === l3Idx ? 'ring-1 ring-purple-400' : '',
                    optDragSrc.level === 3 && optDragSrc.idx === l3Idx ? 'opacity-40' : '',
                  ]"
                  @click="editSelectedL3Idx = l3Idx"
                  @dragstart="onOptDragStart($event, 3, l3Idx)"
                  @dragover="onOptDragOver($event, 3, l3Idx)"
                  @dragleave="onOptDragLeave"
                  @drop="onEditOptDrop($event, 3, l3Idx)"
                  @dragend="onOptDragEnd"
                >
                  <span class="i-lucide-grip-vertical size-3 text-slate-400 cursor-grab shrink-0" />
                  <span class="flex-1 text-sm text-slate-700 dark:text-slate-200 truncate">{{ l3opt.label }}</span>
                  <span v-if="l3opt.children?.length" class="text-xs text-purple-400 shrink-0">▸{{ l3opt.children.length }}</span>
                  <span v-if="editSelectedL3Idx === l3Idx" class="text-xs text-purple-500 shrink-0 font-bold">›</span>
                  <button class="p-0.5 text-slate-300 hover:text-red-500 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" @click.stop="removeEditL3Option(editSelectedL1Idx, editSelectedL2Idx, l3Idx)"><span class="i-lucide-x size-3" /></button>
                </div>
                <p v-if="!editState.options[editSelectedL1Idx]?.children?.[editSelectedL2Idx]?.children?.length" class="text-xs text-slate-400 italic text-center py-4">No level 3 choices yet</p>
              </div>
              <div class="border-t border-purple-100 dark:border-purple-800 px-2 py-2 flex gap-1">
                <input :value="editL3Inputs[`${editSelectedL1Idx}_${editSelectedL2Idx}`] || ''" type="text" placeholder="Add choice…" class="flex-1 min-w-0 px-2 py-1 text-xs rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-woot-500" @input="editL3Inputs = { ...editL3Inputs, [`${editSelectedL1Idx}_${editSelectedL2Idx}`]: $event.target.value }" @keydown.enter.prevent="addEditL3Option(editSelectedL1Idx, editSelectedL2Idx)" />
                <button class="px-2 py-1 text-xs font-medium text-purple-600 border border-purple-200 rounded hover:bg-purple-50 dark:hover:bg-purple-900/20 shrink-0" @click="addEditL3Option(editSelectedL1Idx, editSelectedL2Idx)">+</button>
              </div>
            </div>

            <!-- Column 4: Level 4 choices -->
            <div v-if="editSelectedL1Idx !== null && editSelectedL2Idx !== null && editSelectedL3Idx !== null" class="flex-1 min-w-[185px] flex flex-col rounded-lg border border-pink-200 dark:border-pink-700 bg-white dark:bg-slate-800 overflow-hidden">
              <div class="px-3 py-2 bg-pink-50 dark:bg-pink-900/20 border-b border-pink-200 dark:border-pink-700 space-y-1.5">
                <p class="text-xs font-semibold text-pink-600 dark:text-pink-400 uppercase tracking-wide">Level 4 — under "{{ editState.options[editSelectedL1Idx]?.children?.[editSelectedL2Idx]?.children?.[editSelectedL3Idx]?.label }}"</p>
                <input :value="editState.options[editSelectedL1Idx]?.children?.[editSelectedL2Idx]?.children?.[editSelectedL3Idx]?.level4_label || ''" type="text" placeholder="Label for agents (e.g. Request Name)" class="w-full px-2 py-1 text-xs rounded border border-pink-200 dark:border-pink-700 bg-white dark:bg-slate-700 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-pink-500" @input="updateEditLevel4Label(editSelectedL1Idx, editSelectedL2Idx, editSelectedL3Idx, $event.target.value)" />
              </div>
              <div class="flex-1 overflow-y-auto max-h-52 py-1">
                <div
                  v-for="(l4opt, l4Idx) in (editState.options[editSelectedL1Idx]?.children?.[editSelectedL2Idx]?.children?.[editSelectedL3Idx]?.children || [])"
                  :key="l4Idx"
                  draggable="true"
                  class="group flex items-center gap-1 px-2 py-1.5 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
                  :class="[
                    optDragOver.level === 4 && optDragOver.idx === l4Idx ? 'ring-1 ring-pink-400' : '',
                    optDragSrc.level === 4 && optDragSrc.idx === l4Idx ? 'opacity-40' : '',
                  ]"
                  @dragstart="onOptDragStart($event, 4, l4Idx)"
                  @dragover="onOptDragOver($event, 4, l4Idx)"
                  @dragleave="onOptDragLeave"
                  @drop="onEditOptDrop($event, 4, l4Idx)"
                  @dragend="onOptDragEnd"
                >
                  <span class="i-lucide-grip-vertical size-3 text-slate-400 cursor-grab shrink-0" />
                  <span class="flex-1 text-sm text-slate-700 dark:text-slate-200 truncate">{{ l4opt.label }}</span>
                  <button class="p-0.5 text-slate-300 hover:text-red-500 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" @click="removeEditL4Option(editSelectedL1Idx, editSelectedL2Idx, editSelectedL3Idx, l4Idx)"><span class="i-lucide-x size-3" /></button>
                </div>
                <p v-if="!editState.options[editSelectedL1Idx]?.children?.[editSelectedL2Idx]?.children?.[editSelectedL3Idx]?.children?.length" class="text-xs text-slate-400 italic text-center py-4">No level 4 choices yet</p>
              </div>
              <div class="border-t border-pink-100 dark:border-pink-800 px-2 py-2 flex gap-1">
                <input :value="editL4Inputs[`${editSelectedL1Idx}_${editSelectedL2Idx}_${editSelectedL3Idx}`] || ''" type="text" placeholder="Add choice…" class="flex-1 min-w-0 px-2 py-1 text-xs rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-pink-500" @input="editL4Inputs = { ...editL4Inputs, [`${editSelectedL1Idx}_${editSelectedL2Idx}_${editSelectedL3Idx}`]: $event.target.value }" @keydown.enter.prevent="addEditL4Option(editSelectedL1Idx, editSelectedL2Idx, editSelectedL3Idx)" />
                <button class="px-2 py-1 text-xs font-medium text-pink-600 border border-pink-200 rounded hover:bg-pink-50 dark:hover:bg-pink-900/20 shrink-0" @click="addEditL4Option(editSelectedL1Idx, editSelectedL2Idx, editSelectedL3Idx)">+</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  </div>
</template>

<style scoped>
.btn-add-blue {
  background-color: #c2dcfb !important;
  border-radius: 100px !important;
  box-shadow:
    rgba(37, 99, 235, .2) 0 -25px 18px -14px inset,
    rgba(37, 99, 235, .15) 0 1px 2px,
    rgba(37, 99, 235, .15) 0 2px 4px,
    rgba(37, 99, 235, .15) 0 4px 8px,
    rgba(37, 99, 235, .15) 0 8px 16px,
    rgba(37, 99, 235, .15) 0 16px 32px !important;
  color: #1d4ed8 !important;
  cursor: pointer !important;
  display: inline-block !important;
  font-family: CerebriSans-Regular, -apple-system, system-ui, Roboto, sans-serif !important;
  padding: 7px 20px !important;
  text-align: center !important;
  text-decoration: none !important;
  transition: all 250ms !important;
  border: 0 !important;
  outline: none !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  width: auto !important;
  margin: 0 !important;
  white-space: nowrap !important;
  user-select: none;
  -webkit-user-select: none;
  touch-action: manipulation;
}
.btn-add-blue:hover {
  box-shadow:
    rgba(37, 99, 235, .35) 0 -25px 18px -14px inset,
    rgba(37, 99, 235, .25) 0 1px 2px,
    rgba(37, 99, 235, .25) 0 2px 4px,
    rgba(37, 99, 235, .25) 0 4px 8px,
    rgba(37, 99, 235, .25) 0 8px 16px,
    rgba(37, 99, 235, .25) 0 16px 32px !important;
  transform: scale(1.05) rotate(-1deg) !important;
}
</style>
